import { Badge, Card, CardContent, CardHeader, CardTitle } from "@devhop/ui";
import { AlertTriangleIcon } from "lucide-react";
import type { KPIAssignmentImpact } from "../../domain/types";

interface KPIImpactWarningProps {
  impact: KPIAssignmentImpact;
  onConfirm?: () => void;
  onCancel?: () => void;
}

export function KPIImpactWarning({
  impact,
  onConfirm,
  onCancel,
}: KPIImpactWarningProps) {
  return (
    <Card className="mb-4 border-yellow-500 bg-yellow-50 dark:bg-yellow-950/20">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base text-yellow-900 dark:text-yellow-100">
          <AlertTriangleIcon className="h-5 w-5" />
          ການແກ້ໄຂນີ້ຈະມີຜົນກະທົບ
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div>
          <p className="mb-2 text-sm">
            KPI ນີ້ມີການມອບໝາຍ{" "}
            <Badge variant="secondary" className="mx-1">
              {impact.assignmentCount}
            </Badge>{" "}
            ລາຍການ (ກຳລັງໃຊ້ງານ{" "}
            <Badge variant="default" className="mx-1">
              {impact.activeAssignmentCount}
            </Badge>{" "}
            ລາຍການ)
          </p>
        </div>

        {impact.hasResults && (
          <div className="rounded-md border border-destructive/20 bg-destructive/10 p-3">
            <p className="font-medium text-destructive text-sm">
              ⚠️ ມີຜົນການປະເມີນແລ້ວ: ການແກ້ໄຂນີ້ອາດສົ່ງຜົນກະທົບຕໍ່ຜົນການປະເມີນທີ່ມີຢູ່ແລ້ວ
            </p>
          </div>
        )}

        {impact.affectedEmployees.length > 0 && (
          <div>
            <p className="mb-1 font-medium text-sm">
              ພະນັກງານທີ່ຮັບຜົນກະທົບ ({impact.affectedEmployees.length}
              ):
            </p>
            <div className="flex flex-wrap gap-1">
              {impact.affectedEmployees.map((emp) => (
                <Badge key={emp.id} variant="outline" className="text-xs">
                  {emp.name}
                </Badge>
              ))}
            </div>
          </div>
        )}

        {impact.affectedPositions.length > 0 && (
          <div>
            <p className="mb-1 font-medium text-sm">
              ຕໍາແໜ່ງທີ່ຮັບຜົນກະທົບ ({impact.affectedPositions.length}):
            </p>
            <div className="flex flex-wrap gap-1">
              {impact.affectedPositions.map((pos) => (
                <Badge key={pos.id} variant="outline" className="text-xs">
                  {pos.name}
                </Badge>
              ))}
            </div>
          </div>
        )}

        {impact.affectedDepartments.length > 0 && (
          <div>
            <p className="mb-1 font-medium text-sm">
              ພະແນກທີ່ຮັບຜົນກະທົບ ({impact.affectedDepartments.length}):
            </p>
            <div className="flex flex-wrap gap-1">
              {impact.affectedDepartments.map((dept) => (
                <Badge key={dept.id} variant="outline" className="text-xs">
                  {dept.name}
                </Badge>
              ))}
            </div>
          </div>
        )}

        <div className="pt-2 text-xs text-yellow-800 dark:text-yellow-200">
          ກະລຸນາພິຈາລະນາຜົນກະທົບກ່ອນດຳເນີນການແກ້ໄຂ
        </div>
      </CardContent>
    </Card>
  );
}
